/**
 * @file src/api/expense.api.js
 * @description Purchase documents (bills / operating expenses / office
 * equipment) — one unified resource at /purchases/expenses. documentType
 * distinguishes them.
 */

import { apiClient } from "lib/axios";

const normalizeExpense = (doc) => {
  if (!doc) return doc;
  return {
    ...doc,
    id: doc.id || doc._id,
    vendorId: doc.vendorId?._id || doc.vendorId?.id || doc.vendorId || "",
    vendorName: doc.vendorName || doc.vendorId?.name || "—",
  };
};

const normalizeListResponse = (data) => {
  const documents =
    data?.documents || data?.data?.documents || data?.data || [];
  return {
    ...data,
    documents: Array.isArray(documents) ? documents.map(normalizeExpense) : [],
  };
};

const normalizeItemResponse = (data) => {
  const document = data?.document || data?.data?.document || data?.data || data;
  return { ...data, document: normalizeExpense(document) };
};

const buildCleanPayload = (payload) => {
  const clean = {
    documentType: payload.documentType,
    vendorId: payload.vendorId || null,
    vendorName: payload.vendorName || "",
    category: payload.category || "",
    documentDate: payload.documentDate,
    dueDate: payload.dueDate || null,
    vendorBillNumber: payload.vendorBillNumber || "",
    branchId: payload.branchId || null,
    currency: payload.currency || "SAR",
    notes: payload.notes || "",
    purchaseOrderId: payload.purchaseOrderId || null,
  };

  // Bill path (line items) vs single-amount expense path.
  if (Array.isArray(payload.lineItems) && payload.lineItems.length > 0) {
    clean.lineItems = payload.lineItems;
  } else {
    clean.amount = payload.amount;
    clean.taxRate = payload.taxRate;
    if (payload.accountId) clean.accountId = payload.accountId;
  }

  return clean;
};

export const getExpenses = async (params = {}) => {
  const { data } = await apiClient.get("/purchases/expenses", { params });
  return normalizeListResponse(data);
};

export const getExpenseById = async (id) => {
  const { data } = await apiClient.get(`/purchases/expenses/${id}`);
  return normalizeItemResponse(data);
};

export const createExpense = async (payload) => {
  const { data } = await apiClient.post(
    "/purchases/expenses",
    buildCleanPayload(payload),
  );
  return normalizeItemResponse(data);
};

export const voidExpense = async (id, body = {}) => {
  const { data } = await apiClient.post(`/purchases/expenses/${id}/void`, body);
  return normalizeItemResponse(data);
};
